import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import get from 'lodash/get';
import { connect } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Tooltip from '../atoms/Tooltip';
import { RanksType } from '../atoms/Ranks';
import Loader from '../atoms/Loader';
import { COLOR_LIGHT_ORANGE, COLOR_WHITE } from '../styles/colors';
import { FONT_PROXIMA_NOVA } from '../styles/fonts';
import gql from '../services/gql';
import { inchesToFeetAndInches, secondsToMinutesSecondsMs } from '../services/converter';
import { FormatType, getEventFormatType, getEventName } from '../services/event-names-and-formats';
import { getPlayerPath } from '../services/path';
import { State } from '../redux/reducers';
import Position from '../types/Position';
import StatsPlayer from '../types/StatsPlayer';
import Team from '../types/Team';
import User from '../types/User';

interface RanksTooltipProps {
  type?: RanksType;
  playerId?: number;
  teamId?: number;
  collegeTeamId?: number;
  nflTeamId?: number;
  position?: Position;
  combineId?: number | null;
  combineCategory?: 'hs' | 'nfl' | null;
  comparisonGroup: RanksComparisonGroup | string | undefined;
  comparisonEvent?: 'pai' | 'combine' | 'proCombine' | string;
  rank?: number;
  children: any;
  selectedCollegeTeam: Team | undefined;
  user?: User;
  isNFL?: boolean;
  selectedPPIYear?: number;
}

export enum RanksComparisonGroup {
  COLLEGE_TEAM = 'collegeTeam',
  COLLEGE_CONFERENCE = 'collegeConference',
  NFL_TEAM = 'nflTeam',
  NFL_UDFA = 'nflUDFA',
  NFL_DRAFT = 'nflDraft',
}

const useStyles = makeStyles((theme: Theme) => ({
  ranksTooltip: {},

  table: {
    marginTop: '6px',
    marginBottom: '6px',
    borderCollapse: 'collapse',
    fontFamily: FONT_PROXIMA_NOVA,
    fontSize: theme.typography.pxToRem(12),
    lineHeight: theme.typography.pxToRem(15),
  },
  tableRow: {
    color: COLOR_WHITE,
  },
  tableCell: {
    textAlign: 'center',
    padding: '3px 4px',
    color: 'inherit',

    '&:first-of-type': {
      paddingRight: theme.spacing(1.5),
    },

    '&:last-of-type': {
      paddingLeft: theme.spacing(1.5),
    },
  },

  nameLink: {
    color: 'inherit',
    textDecoration: 'none',

    '&:hover': {
      textDecoration: 'underline',
    },
  },

  emptySpace: {
    height: 100,
  },

  comparisonEvent: {
    textTransform: 'capitalize',
  },

  currentValueRow: {
    color: COLOR_LIGHT_ORANGE,
  },
}), { name: UpdatedRanksTooltip.name });

function UpdatedRanksTooltip (props: RanksTooltipProps) {
  const {
    type = RanksType.PAI,
    playerId,
    teamId,
    collegeTeamId,
    nflTeamId,
    position,
    comparisonGroup,
    combineId = null,
    combineCategory = null,
    comparisonEvent,
    rank,
    selectedCollegeTeam,
    user,
    children,
    isNFL = false,
    selectedPPIYear,
  } = props;
  const classes = useStyles();
  const [open, setOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [players, setPlayers] = useState<StatsPlayer[]>([]);
  const location = useLocation();
  const newProfileEnabled = location.pathname.includes('player-profile/');
  useEffect(() => {
    if (open && !loading && playerId && (collegeTeamId || nflTeamId) && position && rank && comparisonEvent) {
      if (type === RanksType.PAI) {
        isNFL ? fetchNFLPAIPlayers() : fetchPAIPlayers();
      } else if (type === RanksType.PROJECTED_PAI) {
        isNFL ? fetchNFLProjectedPAIPlayers() : fetchProjectedPAIPlayers();
      } else if (type === RanksType.COMBINE) {
        isNFL ? fetchNFLCombinePlayers() : fetchCombinePlayers();
      } else if (type === RanksType.PPI) {
        isNFL ? fetchNFLPPIPlayers() : fetchPPIPlayers();
      } else if (type === RanksType.PRO_COMBINE) {
        // isNFL ? fetchNFLProCombinePlayers() : fetchProCombinePlayers();
        isNFL ? fetchNFLCombinePlayers() : fetchCombinePlayers();
      }
    }
  }, [open, type, playerId, teamId, collegeTeamId, nflTeamId, position, comparisonGroup, combineId, combineCategory, comparisonEvent, rank, isNFL]);

  function fetchPAIPlayers () {
    if (!loading) setLoading(true);
    // changed the fieldname from comparisonGroup to category 
    // comparisonGroup: "${comparisonGroup}",
    gql(`
      getPAIStatsPlayersList (
        playerId: ${playerId},
        compareToTeamId: ${collegeTeamId},
        positionCode: "${position}",
        category: "${comparisonGroup}",
        event: "${comparisonEvent}"
      ) {
        id
        slug
        firstName
        lastName
        teamName
        performance
      }
    `)
      .then((data:any) => get(data, 'getPAIStatsPlayersList') as StatsPlayer[])
      .then((players:StatsPlayer[]) => {
        if (players && players.length) {
          const selectedPlayerIndex = players
            .findIndex((player:StatsPlayer) => playerId === player.id);
          const playersWithOrder = [
            ...players
              .slice(0, selectedPlayerIndex)
              .map((player, index) => ({
                ...player,
                order: (rank || 0) - selectedPlayerIndex + index,
              })),
            ...players
              .slice(selectedPlayerIndex)
              .map((player, index) => ({
                ...player,
                order: (rank || 0) + index,
              })),
          ];

          setPlayers(playersWithOrder);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }

  function fetchProjectedPAIPlayers () {
    if (!loading) setLoading(true);
    // changed the fieldname from comparisonGroup to category 
    // comparisonGroup: "${comparisonGroup}",
    const compEvent = type === RanksType.PROJECTED_PAI ? 'projected-pai' : comparisonGroup;
    gql(`
      getProjectedPAIStatsPlayersList (
        playerId: ${playerId},
        compareToTeamId: ${collegeTeamId},
        positionCode: "${position}",
        category: "${comparisonGroup}",
        event: "${compEvent}"
      ) {
        id
        slug
        firstName
        lastName
        teamName
        performance
      }
    `)
      .then((data:any) => get(data, 'getProjectedPAIStatsPlayersList') as StatsPlayer[])
      .then((players:StatsPlayer[]) => {
        if (players && players.length) {
          const selectedPlayerIndex = players
            .findIndex((player:StatsPlayer) => playerId === player.id);
          const playersWithOrder = [
            ...players
              .slice(0, selectedPlayerIndex)
              .map((player, index) => ({
                ...player,
                order: (rank || 0) - selectedPlayerIndex + index,
              })),
            ...players
              .slice(selectedPlayerIndex)
              .map((player, index) => ({
                ...player,
                order: (rank || 0) + index,
              })),
          ];

          setPlayers(playersWithOrder);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }

  function fetchCombinePlayers () {
    if (!loading) setLoading(true);
    const playerCombineIdField = combineId != null ? `,playerCombineId: ${combineId},` : '';
    // const event = 
    // combineType: "${combineCategory}",
    const event = comparisonEvent === 'proCombine' ? 'nfl-combine' : comparisonEvent;
    gql(`
      getCombineStatsPlayersList (
        playerId: ${playerId},
        compareToTeamId: ${collegeTeamId},
        positionCode: "${position}",
        category: "${comparisonGroup}",
        event: "${event}"
        ${playerCombineIdField}
      ) {
        id
        slug
        firstName
        lastName
        teamName
        performance
      }
    `)
      .then((data:any) => get(data, 'getCombineStatsPlayersList') as StatsPlayer[])
      .then((players:StatsPlayer[]) => {
        if (players && players.length) {
          const selectedPlayerIndex = players
            .findIndex((player:StatsPlayer) => playerId === player.id);
          const playersWithOrder = [
            ...players
              .slice(0, selectedPlayerIndex)
              .map((player, index) => ({
                ...player,
                order: (rank || 0) - selectedPlayerIndex + index,
              })),
            ...players
              .slice(selectedPlayerIndex)
              .map((player, index) => ({
                ...player,
                order: (rank || 0) + index,
              })),
          ];

          setPlayers(playersWithOrder);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }

  function fetchPPIPlayers () {
    if (!loading) setLoading(true);

    gql(`
      getPPIStatsPlayersList (
        playerId: ${playerId},
        compareToTeamId: ${collegeTeamId},
        positionCode: "${position}",
        category: "${comparisonGroup}",
        event: "${comparisonEvent}",
        year: ${selectedPPIYear}
      ) {
        id
        slug
        firstName
        lastName
        teamName
        performance
      }
    `)
      .then((data:any) => data?.getPPIStatsPlayersList as StatsPlayer[])
      .then((players:StatsPlayer[]) => {
        if (!!players?.length) {
          const selectedPlayerIndex = players.findIndex((player:StatsPlayer) => playerId === player.id);
          const playersWithOrder = [
            ...players
              .slice(0, selectedPlayerIndex)
              .map((player, index) => ({
                ...player,
                order: (rank || 0) - selectedPlayerIndex + index,
              })),
            ...players
              .slice(selectedPlayerIndex)
              .map((player, index) => ({
                ...player,
                order: (rank || 0) + index,
              })),
          ];

          setPlayers(playersWithOrder);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }

  function fetchNFLPPIPlayers () {
    if (!loading) setLoading(true);

    gql(`
      getPPIStatsPlayersListNFL (
        playerId: ${playerId},
        compareToCollegeTeamId: ${collegeTeamId},
        compareToNFLTeamId: ${nflTeamId},
        positionCode: "${position}",
        category: "${comparisonGroup}",
        event: "${comparisonEvent}",
        year: ${selectedPPIYear}
      ) {
        id
        slug
        firstName
        lastName
        teamName
        performance
      }
    `)
      .then((data:any) => data?.getPPIStatsPlayersListNFL as StatsPlayer[])
      .then((players:StatsPlayer[]) => {
        if (!!players?.length) {
          const selectedPlayerIndex = players.findIndex((player:StatsPlayer) => playerId === player.id);
          const playersWithOrder = [
            ...players
              .slice(0, selectedPlayerIndex)
              .map((player, index) => ({
                ...player,
                order: (rank || 0) - selectedPlayerIndex + index,
              })),
            ...players
              .slice(selectedPlayerIndex)
              .map((player, index) => ({
                ...player,
                order: (rank || 0) + index,
              })),
          ];

          setPlayers(playersWithOrder);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }

  // function fetchProCombinePlayers () {
  //   if (!loading) setLoading(true);

  //   gql(`
  //     proCombineStatsPlayerListComparison (
  //       id: ${playerId},
  //       compareToTeamId: ${collegeTeamId},
  //       positionCode: "${position}",
  //       playerNFLCombineId: ${combineId},
  //       category: "${comparisonGroup}",
  //       event: "${comparisonEvent}"
  //     ) {
  //       id
  //       slug
  //       firstName
  //       lastName
  //       teamName
  //       performance
  //     }
  //   `)
  //     .then((data:any) => get(data, 'proCombineStatsPlayerListComparison') as StatsPlayer[])
  //     .then((players:StatsPlayer[]) => {
  //       if (players && players.length) {
  //         const selectedPlayerIndex = players
  //           .findIndex((player:StatsPlayer) => playerId === player.id);
  //         const playersWithOrder = [
  //           ...players
  //             .slice(0, selectedPlayerIndex)
  //             .map((player, index) => ({
  //               ...player,
  //               order: (rank || 0) - selectedPlayerIndex + index,
  //             })),
  //           ...players
  //             .slice(selectedPlayerIndex)
  //             .map((player, index) => ({
  //               ...player,
  //               order: (rank || 0) + index,
  //             })),
  //         ];

  //         setPlayers(playersWithOrder);
  //       }
  //     })
  //     .catch(console.error)
  //     .finally(() => setLoading(false));
  // }
  
  function fetchNFLPAIPlayers () {
    if (!loading) setLoading(true);
    // changed the fieldname from comparisonGroup to category 
    // comparisonGroup: "${comparisonGroup}",
    gql(`
      getPAIStatsPlayersListNFL (
        playerId: ${playerId},
        compareToCollegeTeamId: ${collegeTeamId},
        compareToNFLTeamId: ${nflTeamId},
        positionCode: "${position}",
        category: "${comparisonGroup}",
        event: "${comparisonEvent}"
      ) {
        id
        slug
        firstName
        lastName
        teamName
        performance
      }
    `)
      .then((data:any) => get(data, 'getPAIStatsPlayersListNFL') as StatsPlayer[])
      .then((players:StatsPlayer[]) => {
        if (players && players.length) {
          const selectedPlayerIndex = players
            .findIndex((player:StatsPlayer) => playerId === player.id);
          const playersWithOrder = [
            ...players
              .slice(0, selectedPlayerIndex)
              .map((player, index) => ({
                ...player,
                order: (rank || 0) - selectedPlayerIndex + index,
              })),
            ...players
              .slice(selectedPlayerIndex)
              .map((player, index) => ({
                ...player,
                order: (rank || 0) + index,
              })),
          ];

          setPlayers(playersWithOrder);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }

  function fetchNFLProjectedPAIPlayers () {
    if (!loading) setLoading(true);
    // changed the fieldname from comparisonGroup to category 
    // comparisonGroup: "${comparisonGroup}",
    const compEvent = type === RanksType.PROJECTED_PAI ? 'projected-pai' : comparisonGroup;
    gql(`
      getProjectedPAIStatsPlayersListNFL (
        playerId: ${playerId},
        compareToCollegeTeamId: ${collegeTeamId},
        compareToNFLTeamId: ${nflTeamId},
        positionCode: "${position}",
        category: "${comparisonGroup}",
        event: "${compEvent}"
      ) {
        id
        slug
        firstName
        lastName
        teamName
        performance
      }
    `)
      .then((data:any) => get(data, 'getProjectedPAIStatsPlayersListNFL') as StatsPlayer[])
      .then((players:StatsPlayer[]) => {
        if (players && players.length) {
          const selectedPlayerIndex = players
            .findIndex((player:StatsPlayer) => playerId === player.id);
          const playersWithOrder = [
            ...players
              .slice(0, selectedPlayerIndex)
              .map((player, index) => ({
                ...player,
                order: (rank || 0) - selectedPlayerIndex + index,
              })),
            ...players
              .slice(selectedPlayerIndex)
              .map((player, index) => ({
                ...player,
                order: (rank || 0) + index,
              })),
          ];

          setPlayers(playersWithOrder);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }

  function fetchNFLCombinePlayers () {
    if (!loading) setLoading(true);
    // combineType: "${combineCategory}",
    const event = comparisonEvent === 'proCombine' ? 'nfl-combine' : comparisonEvent;
    const playerCombineIdField = combineId != null ? `,playerCombineId: ${combineId},` : '';
    gql(`
      getCombineStatsPlayersListNFL (
        playerId: ${playerId},
        positionCode: "${position}",
        compareToCollegeTeamId: ${collegeTeamId},
        compareToNFLTeamId: ${nflTeamId},
        category: "${comparisonGroup}",
        event: "${event}"
        ${playerCombineIdField}
      ) {
        id
        slug
        firstName
        lastName
        teamName
        performance
      }
    `)
      .then((data:any) => get(data, 'getCombineStatsPlayersListNFL') as StatsPlayer[])
      .then((players:StatsPlayer[]) => {
        if (players && players.length) {
          const selectedPlayerIndex = players
            .findIndex((player:StatsPlayer) => playerId === player.id);
          const playersWithOrder = [
            ...players
              .slice(0, selectedPlayerIndex)
              .map((player, index) => ({
                ...player,
                order: (rank || 0) - selectedPlayerIndex + index,
              })),
            ...players
              .slice(selectedPlayerIndex)
              .map((player, index) => ({
                ...player,
                order: (rank || 0) + index,
              })),
          ];

          setPlayers(playersWithOrder);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }

  // function fetchNFLProCombinePlayers () {
  //   if (!loading) setLoading(true);

  //   gql(`
  //     proCombineStatsPlayerListNFLComparison (
  //       id: ${playerId},
  //       compareToCollegeTeamId: ${collegeTeamId},
  //       compareToNFLTeamId: ${nflTeamId},
  //       positionCode: "${position}",
  //       playerNFLCombineId: ${combineId},
  //       category: "${comparisonGroup}",
  //       event: "${comparisonEvent}"
  //     ) {
  //       id
  //       slug
  //       firstName
  //       lastName
  //       teamName
  //       performance
  //     }
  //   `)
  //     .then((data:any) => get(data, 'proCombineStatsPlayerListNFLComparison') as StatsPlayer[])
  //     .then((players:StatsPlayer[]) => {
  //       if (players && players.length) {
  //         const selectedPlayerIndex = players
  //           .findIndex((player:StatsPlayer) => playerId === player.id);
  //         const playersWithOrder = [
  //           ...players
  //             .slice(0, selectedPlayerIndex)
  //             .map((player, index) => ({
  //               ...player,
  //               order: (rank || 0) - selectedPlayerIndex + index,
  //             })),
  //           ...players
  //             .slice(selectedPlayerIndex)
  //             .map((player, index) => ({
  //               ...player,
  //               order: (rank || 0) + index,
  //             })),
  //         ];

  //         setPlayers(playersWithOrder);
  //       }
  //     })
  //     .catch(console.error)
  //     .finally(() => setLoading(false));
  // }

  function formatPerformance (performance:number, formatType:FormatType):string {
    if (formatType === FormatType.FEET_AND_INCHES) {
      return inchesToFeetAndInches(performance);
    } else if (formatType === FormatType.INCHES) {
      return `${Number(performance).toFixed(2)}"`;
    } else if (formatType === FormatType.MINUTES_SECONDS_MS) {
      return secondsToMinutesSecondsMs(performance);
    }

    return Number(performance).toFixed(2);
  }

  function formatPpiEventName (event:string):string {
    return event
      .split('-')
      .map((word:string) => {
        if (word.toLowerCase() === 'ppi') {
          return 'PPI';
        }

        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(' ');
  }

  function getEventNameForPAIOrPPAI (event:string|undefined, type: RanksType):string {
    const eventNameByType: Record<string, string> = {
      pai: 'PAI',
      proCombine: 'Pro COM',
      combine: 'COM',
    };

    if (event && eventNameByType[event]) {
      return eventNameByType[event];
    }

    if (event?.includes('ppi')) {
      return formatPpiEventName(event);
    }

    return getEventName(event);
  }

  return (
    <Tooltip
      className={classes.ranksTooltip}
      open={open}
      title={(
        <>
          <Loader inProgress={loading} />

          <table className={classes.table}>
            <thead>
            <tr className={classes.tableRow}>
              <th className={classes.tableCell}>Rank</th>
              <th className={classes.tableCell}>Name</th>
              <th className={classes.tableCell}>College</th>
              <th className={clsx(classes.tableCell, classes.comparisonEvent)}>
                {getEventNameForPAIOrPPAI(comparisonEvent, type)}
              </th>
            </tr>
            </thead>

            <tbody>
              {players.map((player:StatsPlayer) => (
                <tr
                  key={player.id}
                  className={clsx(
                    classes.tableRow,
                    playerId === player.id && classes.currentValueRow
                  )}
                >
                  <td className={classes.tableCell}>{(player as any).order}</td>
                  <td className={classes.tableCell}>
                    <Link
                      className={classes.nameLink}
                      to={`${getPlayerPath(player.slug, Boolean(newProfileEnabled))}?`}
                      target='_blank'
                    >
                      {player.firstName} {player.lastName}
                    </Link>
                  </td>
                  <td className={classes.tableCell}>{(comparisonGroup === RanksComparisonGroup.COLLEGE_TEAM || comparisonGroup === 'team-select-team') ? selectedCollegeTeam?.name : player.teamName}</td>
                  <td className={classes.tableCell}>
                    {formatPerformance(player.performance, getEventFormatType(comparisonEvent))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {(!players.length) && (
            <div className={classes.emptySpace} />
          )}
        </>
      )}
      interactive
      placement='bottom'
      onClose={() => setOpen(false)}
    >
      <div onClick={() => setOpen(true)}>
        {children}
      </div>
    </Tooltip>
  );
}

const mapStateToProps = (state:State) => {
  return {
    selectedCollegeTeam: state.ui.selectedCollegeTeam,
    user: state.user,
  };
};

export default connect(
  mapStateToProps,
)(UpdatedRanksTooltip);
